import { createReducer } from "reduxsauce"
import { chain, toUpper, get } from "lodash"
import actionCreator from "helpers/actionCreator"
import { takeEvery, takeLatest, put, call, all, select } from "redux-saga/effects"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Types as SmartTradeTypes } from "modules/smart-trade/model"
import { Types as ResearchTopicTypes } from "modules/research-topic/model"
import { Creators as modelCreators } from "model/data"
import { post as postApi } from "api"
import { sendQuery } from "api/graphql"
import { getSelectedExperimentConfig, getSelectedExperimentPositionSet } from "modules/smart-trade-panel/selectors/experiment"
import { getCompositeSecurityUrl } from "helpers/compositeSecurities"

export const { Types, Creators } = actionCreator("ompnt/modules/smartTradePanel", {
  setSelectedPanelTab: ["tab"],
  setSelectedGroup: ["value"],
  setActiveBasketRule: ["name", "value"],
  removeSecurities: ["securities"],
  resetSecurities: [],
  resetActiveRules: [],
  createSwap: ["ticker", "data"],
  smartTradeRulesSearch: ["search"],
  resetSmartTradeRules: [],
  uploadSwapDateSuccess: [],
  uploadSwapDateError: ["error"],
})

const DEFAULT_STATE = {
  selectedPanelTab: "summary",
  selectedGroup: "topBySize",
  basketRules: null,
  smartTradeRulesSearchValue: null,
  smartTradeRules: null,
  removedSecurities: [],
}

function handleSetSelectedPanelTab(state, { tab }) {
  return { ...state, selectedPanelTab: tab }
}

function handleSetSelectedGroup(state, { value }) {
  return { ...state, selectedGroup: value }
}

function handleSetActiveBasketRule(state, { name, value }) {
  return { ...state, basketRules: { ...state.basketRules, [name]: value } }
}

function handleRemoveSecurities(state, { securities }) {
  return { ...state, removedSecurities: [...state.removedSecurities, ...securities] }
}

function handleApplyRemovedSecurities(state) {
  return { ...state, removedSecurities: [] }
}

function handleResetSecurities(state) {
  return { ...state, removedSecurities: [] }
}

function handleResetActiveRules(state) {
  return { ...state, basketRules: null, smartTradeRules: null }
}

function handleNavigate(state, action) {
  if (!chain(action).get("location.pathname", null).includes("smart-trade").value()) {
    return { ...state, ...DEFAULT_STATE }
  }

  return state
}

function handleSmartTradeRulesSearch(state, { search }) {
  return { ...state, smartTradeRulesSearchValue: search }
}

function handleSetModel(state) {
  return { ...state, ...DEFAULT_STATE }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_SELECTED_PANEL_TAB]: handleSetSelectedPanelTab,
  [Types.SET_SELECTED_GROUP]: handleSetSelectedGroup,
  [Types.SET_ACTIVE_BASKET_RULE]: handleSetActiveBasketRule,
  [Types.REMOVE_SECURITIES]: handleRemoveSecurities,
  [Types.RESET_SECURITIES]: handleResetSecurities,
  [Types.RESET_ACTIVE_RULES]: handleResetActiveRules,
  [Types.SMART_TRADE_RULES_SEARCH]: handleSmartTradeRulesSearch,
  [SmartTradeTypes.SET_MODEL]: handleSetModel,
  [SmartTradeTypes.APPLY_REMOVED_SECURITIES]: handleApplyRemovedSecurities,
  [HistoryTypes.NAVIGATE]: handleNavigate,
})

// data: { description, terminationDate, currencyOverride }
function* createSwap({ ticker, data }) {
  const [account, config, positionSet] = yield all([
    select((state) => state.global.selected.account),
    select(getSelectedExperimentConfig),
    select(getSelectedExperimentPositionSet),
  ])

  try {
    const result = yield call(
      postApi,
      `/api/${account}/swaps`,
      { ticker: toUpper(ticker), config, ...data },
    )

    if (result) {
      yield put(modelCreators.graphqlCreateSuccess("baskets", result))
      yield startUploadSwapDate({
        account,
        ticker,
        // Make sure all positions in the new basket will be positive
        positionSet: {
          ...positionSet,
          equities: chain(positionSet)
            .get("equities")
            .map(equity => ({
              ...equity,
              economicExposure: Math.abs(equity.economicExposure),
            }))
            .value(),
        },
      })
      yield put(modelCreators.resetCollections(["baskets", "resourcesPermissions"]))
    } else {
      yield put(notificationCreators.add("error", null, "An error occurred while creating the basket."))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An error occurred while creating the basket."))
  }
}

function* startUploadSwapDate({ account, ticker, positionSet }) {
  const mutation = `mutation ($ticker: String! $positionSet: PositionSetDateInput!) {
    uploadSwapDate (ticker: $ticker  data: $positionSet) {
      ok
    }
  }`

  try {
    const { data } = yield call(sendQuery, mutation, { ticker, positionSet }, account)
    const result = get(data, "uploadSwapDate.ok")
    if (result) {
      yield put(Creators.uploadSwapDateSuccess())
      yield put(DialogCreators.open({
        title: `Would you like to view ${toUpper(ticker)} basket now?`,
        contentText: "The new basket has been created successfully. Would you like to view it now?",
        cancelText: "Stay in research topic",
        buttons: [{
          text: "View now",
          type: "primary",
          action: ({ navigate }) => navigate(getCompositeSecurityUrl(account, { assetClass: "swap", ticker: toUpper(ticker) })),
        }],
      }))
    } else {
      yield put(Creators.uploadSwapDateError())
      yield put(notificationCreators.add("error", null, "An error occurred while creating the basket."))
    }
  } catch (err) {
    yield put(Creators.uploadSwapDateError(err))
    yield put(notificationCreators.add("error", null, "An error occurred while creating the basket."))
  }
}

function* handleToggleSummaryPanel({ experiment, segment }) {
  if (!experiment && !segment) {
    yield put(Creators.setSelectedPanelTab("summary"))
  }
}

export function* saga() {
  yield takeEvery(Types.CREATE_SWAP, createSwap)
  yield takeLatest(ResearchTopicTypes.TOGGLE_SUMMARY_PANEL, handleToggleSummaryPanel)
}
